import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import TrialBanner from "../components/TrialBanner";

interface TeamMember {
  id: number;
  email: string;
  fullName: string;
  joinedAt: string;
}

interface TeamData {
  id: number;
  name: string;
  ownerEmail: string;
  ownerName: string;
  tier: string;
  memberCount: number;
  members: TeamMember[];
  createdAt: string;
}

const Team = () => {
  const [team, setTeam] = useState<TeamData | null>(null);
  const [loading, setLoading] = useState(true);
  const [teamName, setTeamName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.plan !== "AGENCY") {
      navigate("/pricing");
      return;
    }
    fetchTeam();
  }, [navigate, user?.plan]);

  const fetchTeam = async () => {
    try {
      const response = await api.get("/api/team/my-team");
      setTeam(response.data);
    } catch (err) {
      // No team yet
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setCreating(true);
    try {
      const response = await api.post("/api/team/create", { name: teamName });
      setTeam(response.data);
      setSuccess("Team created successfully!");
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to create team!");
    } finally {
      setCreating(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setInviting(true);
    try {
      await api.post("/api/team/invite", { email: inviteEmail });
      setSuccess("Invitation sent to " + inviteEmail + "!");
      setInviteEmail("");
      fetchTeam();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to send invitation!");
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (memberId: number) => {
    if (!window.confirm("Are you sure you want to remove this member?")) return;
    try {
      await api.delete(`/api/team/remove/${memberId}`);
      setSuccess("Member removed successfully!");
      fetchTeam();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to remove member!");
    }
  };

  const getTierInfo = () => {
    if (!team) return null;
    switch (team.tier) {
      case "INDIVIDUAL":
        return {
          label: "Individual",
          price: "$15/month",
          color: "text-yellow-400",
        };
      case "TEAM":
        return {
          label: "Team (up to 10)",
          price: "$30/month",
          color: "text-purple-400",
        };
      case "UNLIMITED":
        return {
          label: "Unlimited",
          price: "$50/month",
          color: "text-green-400",
        };
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white">
        <Navbar />
        <TrialBanner />
        <div className="flex items-center justify-center py-20">
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Navbar />

      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Team Management 👥</h1>
          <p className="text-gray-400 mt-2">
            Invite team members to share your Agency plan!
          </p>
        </div>

        {error && (
          <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-6 text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-900 border border-green-700 text-green-300 px-4 py-3 rounded-lg mb-6 text-sm">
            {success}
          </div>
        )}

        {!team ? (
          /* Create Team Form */
          <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
            <h2 className="text-xl font-bold mb-6">Create Your Team</h2>
            <form onSubmit={handleCreateTeam} className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Team Name
                </label>
                <input
                  type="text"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="e.g. ABC Media Agency"
                  required
                  className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:border-purple-500 transition"
                />
              </div>
              <button
                type="submit"
                disabled={creating}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-semibold transition"
              >
                {creating ? "Creating..." : "Create Team"}
              </button>
            </form>
          </div>
        ) : (
          <>
            {/* Team Info */}
            <div className="bg-gray-800 rounded-2xl p-6 border border-yellow-600 mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-yellow-400">
                    {team.name}
                  </h2>
                  <p className="text-gray-400 mt-1">
                    Owner: {team.ownerName} ({team.ownerEmail})
                  </p>
                </div>
                <div className="text-right">
                  {getTierInfo() && (
                    <>
                      <p
                        className={`font-bold text-lg ${getTierInfo()?.color}`}
                      >
                        {getTierInfo()?.label}
                      </p>
                      <p className="text-gray-400 text-sm">
                        {getTierInfo()?.price}
                      </p>
                    </>
                  )}
                  <p className="text-gray-400 text-sm mt-1">
                    {team.memberCount} member{team.memberCount !== 1 ? "s" : ""}
                  </p>
                </div>
              </div>
            </div>

            {/* Upgrade Options */}
            <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700 mb-8">
              <h3 className="text-lg font-bold mb-4">Upgrade Your Team Plan</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Individual */}
                <div
                  className={`p-4 rounded-xl border ${team.tier === "INDIVIDUAL" ? "border-yellow-500 bg-yellow-900 tier-current" : "border-gray-700"}`}
                >
                  <h4 className="font-bold text-yellow-400">Individual</h4>
                  <p className="text-2xl font-bold mt-1">
                    $15
                    <span className="text-gray-400 text-sm tier-sub">/mo</span>
                  </p>
                  <p className="text-gray-400 text-xs mt-1 tier-sub">
                    Just you alone
                  </p>
                  {team.tier === "INDIVIDUAL" && (
                    <span className="text-yellow-400 text-xs mt-2 block">
                      ✓ Current Plan
                    </span>
                  )}
                </div>

                {/* Team */}
                <div
                  className={`p-4 rounded-xl border ${team.tier === "TEAM" ? "border-purple-500 bg-purple-900 tier-current" : "border-gray-700"}`}
                >
                  <h4 className="font-bold text-purple-400">Team</h4>
                  <p className="text-2xl font-bold mt-1">
                    $30
                    <span className="text-gray-400 text-sm tier-sub">/mo</span>
                  </p>
                  <p className="text-gray-400 text-xs mt-1 tier-sub">
                    Up to 10 members
                  </p>
                  {team.tier === "TEAM" ? (
                    <span className="text-purple-400 text-xs mt-2 block">
                      ✓ Current Plan
                    </span>
                  ) : (
                    team.tier === "INDIVIDUAL" && (
                      <button
                        onClick={() => navigate("/pricing")}
                        className="w-full mt-2 bg-purple-600 hover:bg-purple-700 text-white py-1 rounded-lg text-xs transition"
                      >
                        Upgrade
                      </button>
                    )
                  )}
                </div>

                {/* Unlimited (the highest plan, so no Upgrade button) */}
                <div
                  className={`p-4 rounded-xl border ${team.tier === "UNLIMITED" ? "border-green-500 bg-green-900 tier-current" : "border-gray-700"}`}
                >
                  <h4 className="font-bold text-green-400">Unlimited</h4>
                  <p className="text-2xl font-bold mt-1">
                    $50
                    <span className="text-gray-400 text-sm tier-sub">/mo</span>
                  </p>
                  <p className="text-gray-400 text-xs mt-1 tier-sub">
                    Unlimited members
                  </p>
                  {team.tier === "UNLIMITED" && (
                    <span className="text-green-400 text-xs mt-2 block">
                      ✓ Current Plan
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Pricing Warning: both lines yellow */}
            {team.tier === "INDIVIDUAL" && (
              <div className="bg-yellow-900 border border-yellow-600 text-yellow-300 px-4 py-3 rounded-lg mb-6 text-sm">
                ⚠️ Your current plan does not support team members!
                <button
                  onClick={() => navigate("/pricing")}
                  className="ml-2 underline text-yellow-300 font-semibold warning-link"
                >
                  Upgrade to Team ($30/month) or Unlimited ($50/month) on
                  Pricing page
                </button>
              </div>
            )}

            {team.tier === "TEAM" && team.memberCount >= 10 && (
              <div className="bg-yellow-900 border border-yellow-600 text-yellow-300 px-4 py-3 rounded-lg mb-6 text-sm">
                ⚠️ You have reached the 10 member limit!
                <button
                  onClick={() => navigate("/pricing")}
                  className="ml-2 underline text-yellow-300 font-semibold warning-link"
                >
                  Upgrade to Unlimited ($50/month) on Pricing page
                </button>
              </div>
            )}

            {/* Invite Member */}
            <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700 mb-8">
              <h3 className="text-lg font-bold mb-4">Invite Team Member</h3>
              <form onSubmit={handleInvite} className="flex gap-3">
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="teammate@example.com"
                  required
                  className="flex-1 bg-gray-900 border border-gray-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:border-purple-500 transition"
                />
                <button
                  type="submit"
                  disabled={inviting}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-xl font-semibold transition"
                >
                  {inviting ? "Sending..." : "Invite"}
                </button>
              </form>
            </div>

            {/* Team Members */}
            <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
              <h3 className="text-lg font-bold mb-4">
                Team Members ({team.memberCount})
              </h3>
              {team.members.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-4xl mb-3">👥</p>
                  <p className="text-gray-400">
                    No members yet — invite your team!
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {team.members.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between bg-gray-900 rounded-xl p-4 border border-gray-700"
                    >
                      <div>
                        <p className="font-semibold">{member.fullName}</p>
                        <p className="text-gray-400 text-sm">{member.email}</p>
                        <p className="text-gray-500 text-xs mt-1">
                          Joined{" "}
                          {new Date(member.joinedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveMember(member.id)}
                        className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-lg text-sm transition"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Team;
